import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MOMENTOS } from "@/momentos";
import MomentoConciencia from "@/components/MomentoConciencia";

const Ctx = createContext({ pedir: () => {}, listo: false, hayMomento: false });
export const useMomentos = () => useContext(Ctx);

export function MomentosProvider({ children }) {
  const { user } = useAuth();
  const [vistos, setVistos] = useState(null);        // null = todavía no cargó
  const [empresaNombre, setEmpresaNombre] = useState("");
  const [activo, setActivo] = useState(null);        // {id, vars, afterClose}

  const vistosRef = useRef(null);
  const activoRef = useRef(false);
  const cooldownRef = useRef(false);                 // "nunca dos seguidos"
  const pendienteRef = useRef(null);                 // pedido que llegó antes de cargar el estado
  const empresaRef = useRef("");
  const userRef = useRef(null);
  userRef.current = user;
  empresaRef.current = empresaNombre;

  useEffect(() => {
    let alive = true;
    if (!user) { setVistos(null); vistosRef.current = null; return; }
    (async () => {
      try {
        const { data } = await api.get("/momentos/estado");
        if (!alive) return;
        setVistos(data.vistos || []);
        vistosRef.current = data.vistos || [];
        setEmpresaNombre(data.empresa_nombre || "");
      } catch {
        if (alive) { setVistos([]); vistosRef.current = []; }
      }
    })();
    return () => { alive = false; };
  }, [user]);

  const evaluar = useCallback((id, vars, afterClose) => {
    const u = userRef.current;
    if (!MOMENTOS[id] || !u) return false;
    if (vistosRef.current === null) return false;
    if (vistosRef.current.includes(id)) return false;
    if (activoRef.current || cooldownRef.current) return false;
    activoRef.current = true;
    setActivo({ id, vars: { empresa: empresaRef.current, ...vars, nombre: (u.nombre || "").split(" ")[0] }, afterClose });
    return true;
  }, []);

  // pedir: si el estado todavía no cargó, guarda el pedido y se procesa cuando cargue.
  const pedir = useCallback((id, vars = {}, afterClose = null) => {
    if (!MOMENTOS[id]) return false;
    if (vistosRef.current === null) { pendienteRef.current = { id, vars, afterClose }; return false; }
    return evaluar(id, vars, afterClose);
  }, [evaluar]);

  // Cuando el estado termina de cargar, procesar un pedido que haya quedado pendiente.
  useEffect(() => {
    if (vistos !== null && pendienteRef.current) {
      const p = pendienteRef.current;
      pendienteRef.current = null;
      evaluar(p.id, p.vars, p.afterClose);
    }
  }, [vistos, evaluar]);

  const cerrar = useCallback(async ({ salteado, segundos, respuesta }) => {
    setActivo((cur) => {
      if (!cur) return null;
      // marca visto localmente y en el server
      vistosRef.current = [...new Set([...(vistosRef.current || []), cur.id])];
      setVistos(vistosRef.current);
      activoRef.current = false;
      cooldownRef.current = true;
      setTimeout(() => { cooldownRef.current = false; }, 2500);
      (async () => {
        try {
          await api.post("/momentos/visto", { momento: cur.id, salteado, segundos });
          if (cur.id === "m6" && respuesta) await api.post("/momentos/tiempo-recuperado", { texto: respuesta });
        } catch {}
        if (cur.afterClose) cur.afterClose();
      })();
      return null;
    });
  }, []);

  return (
    <Ctx.Provider value={{ pedir, listo: vistos !== null, vistos: vistos || [], hayMomento: activo !== null }}>
      {children}
      {activo && <MomentoConciencia momento={MOMENTOS[activo.id]} vars={activo.vars} onCerrar={cerrar} />}
    </Ctx.Provider>
  );
}
